import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getCalendarTagFilterMode,
  getSessionRegistrationSettings,
  getSessionCancellationSettings,
  updateCalendarTagFilterMode,
  updateSessionRegistrationSettings,
  updateSessionCancellationSettings,
} from "@/lib/api";
import { getErrorMessage } from "@/lib/error-messages";
import { queryKeys } from "@/lib/query-keys";

const settingsSchema = z.object({
  booking_hours: z
    .string()
    .refine(
      (value) => Number.isInteger(Number(value)) && Number(value) >= 0,
      "Bitte eine ganze Zahl ab 0 eingeben.",
    ),
  cancellation_hours: z
    .string()
    .refine(
      (value) => Number.isInteger(Number(value)) && Number(value) >= 0,
      "Bitte eine ganze Zahl ab 0 eingeben.",
    ),
  calendar_tag_filter_mode: z.enum(["buttons", "popover"]),
});

type SettingsForm = z.infer<typeof settingsSchema>;

export function AdminSettingsPage() {
  const queryClient = useQueryClient();
  const registrationSettingsQuery = useQuery({
    queryKey: queryKeys.sessionRegistrationSettings,
    queryFn: getSessionRegistrationSettings,
  });
  const cancellationSettingsQuery = useQuery({
    queryKey: queryKeys.sessionCancellationSettings,
    queryFn: getSessionCancellationSettings,
  });
  const calendarTagFilterModeQuery = useQuery({
    queryKey: queryKeys.calendarTagFilterMode,
    queryFn: getCalendarTagFilterMode,
  });

  const form = useForm<SettingsForm>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      booking_hours: "0",
      cancellation_hours: "12",
      calendar_tag_filter_mode: "buttons",
    },
  });

  useEffect(() => {
    if (
      !registrationSettingsQuery.data ||
      !cancellationSettingsQuery.data ||
      !calendarTagFilterModeQuery.data
    ) {
      return;
    }

    form.reset({
      booking_hours: String(registrationSettingsQuery.data.hours),
      cancellation_hours: String(cancellationSettingsQuery.data.hours),
      calendar_tag_filter_mode: calendarTagFilterModeQuery.data,
    });
  }, [
    calendarTagFilterModeQuery.data,
    cancellationSettingsQuery.data,
    form,
    registrationSettingsQuery.data,
  ]);

  const saveMutation = useMutation({
    mutationFn: async (values: SettingsForm) => {
      const [registration, cancellation, calendarTagFilterMode] =
        await Promise.all([
          updateSessionRegistrationSettings(Number(values.booking_hours)),
          updateSessionCancellationSettings(Number(values.cancellation_hours)),
          updateCalendarTagFilterMode(values.calendar_tag_filter_mode),
        ]);

      return { registration, cancellation, calendarTagFilterMode };
    },
    onSuccess: async (data) => {
      form.reset({
        booking_hours: String(data.registration.hours),
        cancellation_hours: String(data.cancellation.hours),
        calendar_tag_filter_mode: data.calendarTagFilterMode,
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.sessionRegistrationSettings,
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.sessionCancellationSettings,
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.calendarTagFilterMode,
        }),
      ]);
      toast.success("Einstellung erfolgreich gespeichert.");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const isLoading =
    registrationSettingsQuery.isLoading ||
    cancellationSettingsQuery.isLoading ||
    calendarTagFilterModeQuery.isLoading;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Einstellungen</h1>
        <p className="text-sm text-muted-foreground">
          Lege Buchungs- und Stornierungsfristen sowie die Darstellung des
          Mitglieder-Kalenders fest.
        </p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((values) => saveMutation.mutate(values))}
          className="space-y-6"
        >
          <div className="grid max-w-5xl gap-6 lg:grid-cols-2">
            <Card className="w-full">
              <CardHeader>
                <CardTitle>Buchungsfrist</CardTitle>
                <CardDescription>
                  Definiere, wie viele Stunden vor Kursbeginn ein Termin noch
                  gebucht werden kann.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Nach Ablauf dieser Frist kann der Termin für einen Teilnehmer
                  nur noch von einem Trainer oder Admin gebucht werden.
                </p>
                {isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : (
                  <FormField
                    control={form.control}
                    name="booking_hours"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="session-registration-hours">
                          Stunden vor Terminbeginn
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="session-registration-hours"
                            type="number"
                            min={0}
                            step={1}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>

            <Card className="w-full">
              <CardHeader>
                <CardTitle>Stornierungsfrist</CardTitle>
                <CardDescription>
                  Definiere, wie viele Stunden vor Kursbeginn die
                  Selbst-Stornierung endet.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Nach Ablauf dieser Frist kann der Termin für einen Teilnehmer
                  nur noch von einem Trainer oder Admin storniert werden.
                </p>
                {isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : (
                  <FormField
                    control={form.control}
                    name="cancellation_hours"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="session-cancellation-hours">
                          Stunden vor Terminbeginn
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="session-cancellation-hours"
                            type="number"
                            min={0}
                            step={1}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>

            <Card className="w-full">
              <CardHeader>
                <CardTitle>Kalender-Filter</CardTitle>
                <CardDescription>
                  Lege fest, wie Mitglieder die Kurs-Tags im Kalender filtern.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Als Buttongruppe sind alle Tags direkt im Kalender-Header
                  sichtbar. Alternativ liegen sie hinter einem Filter-Button.
                </p>
                {isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : (
                  <FormField
                    control={form.control}
                    name="calendar_tag_filter_mode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Darstellung</FormLabel>
                        {/* Radix' hidden native select reports "" while its options are not rendered yet; that would wipe the loaded value. */}
                        <Select value={field.value} onValueChange={(value) => value && field.onChange(value)}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="buttons">
                              Buttongruppe im Kalender-Header
                            </SelectItem>
                            <SelectItem value="popover">
                              Filter-Button mit Auswahlliste
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {!isLoading && (
            <div className="max-w-5xl">
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? "Wird gespeichert..." : "Speichern"}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}
